/* Components */
import { getLayout } from '@components/PageLayouts/Layout/Standard';
import LocationSkuPageContainer from '@scenes/LocationPage/LocationSkuPage';
import LocationGenericPageContainer from '@scenes/LocationPage/LocationPageGeneric';
import LocationRedirectPage from '@scenes/LocationPage/LocationPageRedirect';

/* Integrations */
import { ssrGQLContentfulQuery } from '@integrations/contentful/contentful.hooks';
import { getLocationStateCollection, getLocationStateCollectionBySlug } from '@integrations/contentful/queries/location/getLocalStateCollection';
import { getGeoCityCollectionByStateSlug, getLocalCityCollection } from '@integrations/contentful/queries/location/getLocalCityCollection';
import { getCustomSkuTemplateCollection, getDefaultSkuTemplateCollection } from '@integrations/contentful/queries/location/getSkuTemplateCollection';
import { getUnitedStatesLocationTemplate } from '@integrations/contentful/queries/location/getUnitedStatesCollection';

/* Constants */
import { PAGE_TYPE_LOCATION } from '@constants/pages';
import { FOURTEEN_DAYS } from '@constants/CacheRevalidate';

/* Types */
import { GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import {
  LocationResponseCity,
  LocationResponseCountry,
  LocationResponseCustomSku,
  LocationResponseDefaultSku,
  LocationResponseState,
  PageDataAPI,
} from '@scenes/LocationPage/locationPage.types';

interface ParamsProps extends ParsedUrlQuery {
  location: Array<string>;
}

const PageTypeSku = 'sku';
const PageTypeGeo = 'geo';

const returnCity = (isSkuType: boolean, location: string[]) => {
  let loc = location;
  const isArray = Array.isArray(loc);
  /* old seo urls have tech-support in it - get it out */
  if (isArray && loc.includes('tech-support')) {
    loc = loc.filter(l => l !== 'tech-support');
  }
  if ((isArray && loc.length < 2) || !isArray) return null;
  if (isSkuType) return loc.slice(0, -1);
  /*  [ state, county, city, bad sku ]
       ie [ca , san-diego, el-cajon, tune-up-a-computer-laptop ]
       ie [nc, forsyth, winston-salem, south-central, old-salem, networking-internet-and-connectivity-support-09]
  */
  if (loc.length >= 4) return [loc[2], loc[1]];
  // [ state, county, city ] or [ state, county, bad sku ]
  if (loc.length === 3) return [loc[2], loc[1]];

  // [ state, city ] or [ state, bad sku ]
  if (loc.length === 2) return [loc[1]];

  return null;
};

function LocationPage(props: any) {
  if (props?.pageType?.includes(PageTypeSku)) {
    return <LocationSkuPageContainer {...props} />;
  }

  if (props?.pageType?.includes(PageTypeGeo)) {
    return <LocationGenericPageContainer {...props} />;
  }

  /* Looks like a junk page. Redirect */
  return <LocationRedirectPage />;
}

LocationPage.pageAttributes = { page: PAGE_TYPE_LOCATION };
LocationPage.Layout = getLayout({ promo: true, footer: true });

export default LocationPage;

export const getStaticProps: GetStaticProps = async context => {
  const { location = [] } = context.params as ParamsProps;
  const [skuPathRaw] = location.slice(-1) || [''];
  const skuPath = skuPathRaw?.toLowerCase();

  /* ----- Base Constants ------- */
  let isSkuType = false;
  let ratingAttributes = null;
  let reviews = [];
  const pageTypes = [];
  const pageDataAPI: PageDataAPI = {
    cityData: null,
    stateData: null,
    countryData: null,
    skuData: null,
    geoList: null,
    stats: null,
    geoCoordinates: null,
  };

  /* -- SKU VIEW ? -- */
  if ((location.length > 1 && skuPath) || (skuPath && location.length === 1)) {
    /* Get sku details from seoName */
    const skuRes = await fetch(`${process.env.NEXT_PUBLIC_API_HOST_FULL_URL}/api/clients/skus/${skuPath}`);
    const sku = await skuRes.json();

    if (sku && sku.sku?.id) {
      isSkuType = true;

      /* Get reviews */
      const reviewRes = await fetch(`${process.env.NEXT_PUBLIC_API_HOST_FULL_URL}/api/clients/reviews/${sku.id}`);
      reviews = await reviewRes.json();
      ratingAttributes = sku?.sku.ratingAttributes || null;
      const { sku: { skuBullets = [], startsAtPrice = '', image = '', meta = '', id = '', name = '', seoName = '' } = {} } = sku;
      pageDataAPI.skuData = { skuBullets, startsAtPrice, image, meta, id, name, seoName };
      pageTypes.push(PageTypeSku);
    }
  }

  /**
   * PATH CONSTANTS
   * @type {any[]}
   */
  const locationPathing = isSkuType ? location.slice(0, -1) : location;
  // Paths
  const [statePath] = locationPathing;
  const cityPath = returnCity(isSkuType, location);

  /**
   * **************************************************
   * DATA BY PARAMS
   * **************************************************
   */

  /* -- Contentful: CUSTOM/DEFAULT SKU DATA -- */
  if (isSkuType && pageDataAPI.skuData) {
    const [
      {
        data: {
          customSkuTemplateCollection: { items: customSkuItems },
        },
      },
      {
        data: {
          defaultSkuTemplateCollection: { items: defaultSkuItems },
        },
      },
    ] = await Promise.all([
      ssrGQLContentfulQuery<LocationResponseCustomSku>({
        query: getCustomSkuTemplateCollection(skuPath),
      }),
      ssrGQLContentfulQuery<LocationResponseDefaultSku>({
        query: getDefaultSkuTemplateCollection(),
      }),
    ]);

    // @ts-ignore
    pageDataAPI.skuData.contentful = customSkuItems.length
      ? { ...customSkuItems[0] }
      : { ...defaultSkuItems[0], id: pageDataAPI.skuData.id, displayName: pageDataAPI.skuData.name, slug: skuPath };

    /* Normalize the skuData so it has the same patterns as local only */
    const contentfulSku = pageDataAPI.skuData.contentful!;

    pageDataAPI.skuData.contentful!.metaData = {
      title: contentfulSku.metaTitle,
      description: contentfulSku.metaDescription,
      keywords: contentfulSku.metaKeywords,
    };
    pageDataAPI.skuData.contentful!.contentBlocks = {
      contentOneTitle: contentfulSku.contentOneTitle,
      contentOneBody: contentfulSku.contentOneBody,
      contentTwoTitle: contentfulSku.contentTwoTitle,
      contentTwoBody: contentfulSku.contentTwoBody,
      contentThreeTitle: contentfulSku.contentThreeTitle,
      contentThreeBody: contentfulSku.contentThreeBody,
    };
  }

  /* -- CITY DATA -- */
  if (Array.isArray(cityPath)) {
    const groupedResponse = await Promise.all(
      cityPath.filter(Boolean).map(async city =>
        ssrGQLContentfulQuery<LocationResponseCity>({
          query: getLocalCityCollection({
            city,
            state: statePath,
          }),
        }),
      ),
    );

    const firstValidCityMatch = groupedResponse.find(({ data }) => data.locationCityCollection.items.length);

    pageDataAPI.cityData = firstValidCityMatch?.data.locationCityCollection?.items?.[0] || null;
    // when we get coordinates for city/state
    // if (pageDataAPI.cityData) {
    //   const coords = {
    //     city: pageDataAPI.cityData.displayName,
    //     // @ts-ignore
    //     state: pageDataAPI.cityData.state?.displayName,
    //   }
    //    const coordinates = await fetch(`${process.env.NEXT_PUBLIC_API_HOST_FULL_URL}/api/geo/geocode?city=${coords.city}&state=${coords.state}`);
    //    pageDataAPI.geoCoordinates = await coordinates.json();
    // };

    pageDataAPI.geoList = null;

    if (pageDataAPI.cityData) {
      pageTypes.push(PageTypeGeo);
    }
  }

  /* -- STATE DATA -- */
  if (statePath && !pageDataAPI.cityData) {
    const [
      {
        data: { locationStateCollection },
      },
      {
        data: { locationCityCollection },
      },
    ] = await Promise.all([
      ssrGQLContentfulQuery<LocationResponseState>({
        query: getLocationStateCollectionBySlug(statePath),
      }),
      ssrGQLContentfulQuery({ query: getGeoCityCollectionByStateSlug(statePath) }),
    ]);

    pageDataAPI.stateData = locationStateCollection?.items?.[0] || null;
    pageDataAPI.geoList = locationCityCollection?.items || null;

    if (pageDataAPI.stateData) {
      pageTypes.push(PageTypeGeo);
    }
  }

  /*
  ERROR 308 (301)
  Now, if we made it here, we checked for city. But if we have a city, we need to
  make sure we aren't paired with a 3 length and no sku. So, we know a county is munched in there.
  /us/state/county/city

  PERF UPDATE: We could frontload the data for the cities at build time (just the cities) and compare to that
               instead of hitting the Contentful API, all the time.
*/
  const { cityData, stateData, skuData } = pageDataAPI;

  if (
    /* some old crust url */
    location.includes('tech-support') ||
    /* We are in a legacy view */
    location.length >= 4 ||
    /* /state/badCounty/city */
    (location.length === 3 && cityData && !skuData) ||
    /* /state/badCounty/badCity */
    (location.length === 3 && !cityData && !skuData) ||
    /* /state/badCity/goodSku */
    (location.length === 3 && !cityData && skuData) ||
    /* We have a potential state, but what is occupying the sku/city space is neither */
    (location.length === 2 && !cityData && !skuData) ||
    /* Not likely, but testing for it. Has sku, bug no legitimate state */
    (location.length === 2 && !cityData && !stateData && skuData)
  ) {
    let locationSlug = null;
    let skuSeoName = null;

    /* Get min location requirement */
    if (stateData) {
      locationSlug = stateData.url;
    }

    /* Override location requirement with city */
    if (cityData) {
      locationSlug = cityData.url;
    }

    /* Can we derive the sku */
    if (skuData) {
      skuSeoName = skuData.seoName;
    }

    /* We can derive something, redirect */
    if (locationSlug) {
      const directories = [locationSlug, skuSeoName].filter(Boolean).join('/');
      return {
        redirect: {
          destination: `/${directories}`,
          permanent: true,
        },
      };
    }

    /* We can derive skuName, redirect */
    if (skuSeoName) {
      return {
        redirect: {
          destination: `/us/${skuSeoName}`,
          permanent: true,
        },
      };
    }

    /* Legitimate error page 404 */
    return {
      notFound: true,
    };
  }
  /* End 308 or 404 */

  /*
  ERROR 404
  Now, if we made it here, we checked for state. So, we either have a bad state or combo with a city/sku.
*/
  if (
    /* We have something but its neither a state or sku, error */
    location.length === 1 &&
    !pageDataAPI.stateData &&
    !pageDataAPI.skuData
  ) {
    return {
      redirect: {
        destination: `/us`,
        permanent: true,
      },
    };
  }
  /* End 404 */

  /**
   * Last ditch effort to get at location data:
   *
   * Do we have Sku data but no location, then we must be in /us/sku
   * Because of how dynamic routes work AND we have an index file. This means, for
   * this instance, we need to get "us" with a "sku". So, only get the country data
   * if we have a sku but no other location data.
   */
  if (pageDataAPI.skuData && !pageDataAPI.cityData && !pageDataAPI.stateData) {
    const [
      {
        data: { unitedStatesLocationTemplateCollection },
      },
      {
        data: { locationStateCollection },
      },
    ] = await Promise.all([
      ssrGQLContentfulQuery<LocationResponseCountry>({
        query: getUnitedStatesLocationTemplate(),
      }),
      ssrGQLContentfulQuery({ query: getLocationStateCollection() }),
    ]);

    pageDataAPI.countryData = unitedStatesLocationTemplateCollection?.items?.[0] || null;
    pageDataAPI.geoList = locationStateCollection?.items || null;

    if (pageDataAPI.stateData) {
      pageTypes.push(PageTypeGeo);
    }
  }
  /**
   * If we have a sku, we have reviews/stats. If not, lets gets some for the location display.
   */
  if (!isSkuType) {
    let skuReviews;
    let stats;
    const [reviewRes, statRes] = await Promise.allSettled([
      fetch(`${process.env.NEXT_PUBLIC_API_HOST_FULL_URL}/api/clients/reviews`),
      fetch(`${process.env.NEXT_PUBLIC_API_HOST_FULL_URL}/api/clients/stats`),
    ]);

    if (reviewRes.status === 'fulfilled') {
      try {
        skuReviews = await reviewRes.value.json();
      } catch (err) {
        skuReviews = [];
      }
    }

    if (statRes.status === 'fulfilled') {
      try {
        stats = await statRes.value.json();
      } catch (err) {
        stats = { stats: { averageReviewsCount: 4.8, completedServicesCount: 11500 } };
      }
    }
    reviews = skuReviews;
    pageDataAPI.stats = stats;
  }

  const cacheDate = `cached on: ${new Date().toString()}`;

  return {
    props: {
      cacheDate,
      pageType: pageTypes,
      locationData: pageDataAPI.cityData ? pageDataAPI.cityData : pageDataAPI.stateData || pageDataAPI.countryData,
      skuData: pageDataAPI.skuData,
      geoList: pageDataAPI.geoList,
      geoCoordinates: pageDataAPI.geoCoordinates,
      ratingAttributes,
      reviews,
      stats: pageDataAPI.stats,
    },
    revalidate: FOURTEEN_DAYS,
  };
};

/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * - Get navigatable service skus
 * - Pluck out just the seoName for pathing for routes
 *
 * @returns {Promise<{paths: RouteSkuParams<string>[] | undefined; fallback: boolean}>}
 */
export async function getStaticPaths<GetStaticPaths>() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
